import { GuidedResultDetail } from './guided-result-detail';
import { isGuidedResult } from './guided-result';
import { Fragment, useState } from 'react';
import { extractAttributes, extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { evalService, type EvalRun } from '@/services/eval-service';
import { skillTemplateService, type SkillTemplate } from '@/services/skill-template-service';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  toast,
} from 'prosci-ui';
import { Plus, FileText } from '@/lib/icons';
import { format } from 'date-fns';

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  running: 'secondary',
  completed: 'default',
  failed: 'destructive',
};

function hasDetail(details: Record<string, unknown> | undefined): boolean {
  return !!details?.reasoning || isGuidedResult(details);
}

export function EvalRunsTab() {
  const [showNewRun, setShowNewRun] = useState(false);
  const [runName, setRunName] = useState('');
  const [runSkillId, setRunSkillId] = useState('');
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [expandedResultId, setExpandedResultId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['evalRuns'],
    queryFn: () => evalService.listRuns(),
    refetchInterval: 10000, // poll for running status
  });

  const { data: expandedRunData } = useQuery({
    queryKey: ['evalRun', expandedRunId],
    queryFn: () => evalService.getRun(expandedRunId!),
    enabled: !!expandedRunId,
  });

  const { data: skillsData } = useQuery({
    queryKey: ['skillTemplates'],
    queryFn: () => skillTemplateService.list(),
  });

  const skills: SkillTemplate[] =
    extractAttributesList(skillsData);

  const createMutation = useMutation({
    mutationFn: ({ name, skill_template_id }: { name: string; skill_template_id?: string }) =>
      evalService.createRun({ name, skill_template_id: skill_template_id || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalRuns'] });
      toast.success('Eval run started');
      setShowNewRun(false);
      setRunName('');
      setRunSkillId('');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to start run'),
  });

  const runs: EvalRun[] =
    extractAttributesList(data);

  const expandedRun: EvalRun | null = extractAttributes(expandedRunData?.data);

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowNewRun(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Eval Run
        </Button>
      </div>

      {isLoading ? (
        <Empty><Spinner /></Empty>
      ) : runs.length === 0 ? (
        <Empty>
          <EmptyIcon><FileText className="h-12 w-12" /></EmptyIcon>
          <EmptyTitle>No eval runs</EmptyTitle>
          <EmptyDescription>Start an eval run to test Kaiya against your test cases.</EmptyDescription>
          <Button onClick={() => setShowNewRun(true)} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            New Eval Run
          </Button>
        </Empty>
      ) : (
        <div className="space-y-4">
          {runs.map((run) => (
            <Card key={run.id}>
              <CardHeader
                className="cursor-pointer"
                onClick={() => {
                  setExpandedRunId(expandedRunId === run.id ? null : run.id);
                  setExpandedResultId(null);
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{run.name}</CardTitle>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      {run.created_at && (
                        <span>{format(new Date(run.created_at), 'MMM d, yyyy HH:mm')}</span>
                      )}
                      {run.total_cases > 0 && (
                        <span>
                          {run.passed_cases}/{run.total_cases} passed
                        </span>
                      )}
                      {run.skill_template_name ? (
                        <Badge variant="outline">{run.skill_template_name}</Badge>
                      ) : (
                        <Badge variant="secondary">All Skills</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {run.overall_score != null && (
                      <div className="text-right">
                        <div className="text-lg font-bold">
                          {(run.overall_score * 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-muted-foreground">Avg Score</div>
                      </div>
                    )}
                    <Badge variant={STATUS_VARIANTS[run.status] || 'outline'}>
                      {run.status}
                    </Badge>
                  </div>
                </div>
              </CardHeader>

              {expandedRunId === run.id && expandedRun && (
                <CardContent>
                  <div className="grid grid-cols-5 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-xl font-bold">{expandedRun.total_cases}</div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-600">{expandedRun.passed_cases}</div>
                      <div className="text-xs text-muted-foreground">Passed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600">{expandedRun.failed_cases}</div>
                      <div className="text-xs text-muted-foreground">Failed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold">
                        {expandedRun.overall_score != null
                          ? `${(expandedRun.overall_score * 100).toFixed(0)}%`
                          : '-'}
                      </div>
                      <div className="text-xs text-muted-foreground">Avg Score</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold">
                        {expandedRun.summary_stats?.avg_latency_ms != null
                          ? `${expandedRun.summary_stats.avg_latency_ms}ms`
                          : '-'}
                      </div>
                      <div className="text-xs text-muted-foreground">Avg Latency</div>
                    </div>
                  </div>

                  {expandedRun.status === 'running' && (
                    <div className="mb-3 rounded-md bg-muted px-4 py-2.5 text-xs text-muted-foreground">
                      Still running — {expandedRun.results?.length ?? 0} of {expandedRun.total_cases}{' '}
                      scored so far. Cases appear as each one finishes.
                    </div>
                  )}

                  <div className="mb-4 flex items-baseline gap-2 text-sm">
                    <span className="text-muted-foreground">Ran as</span>
                    {expandedRun.configuration?.ran_as ? (
                      <span>
                        {expandedRun.configuration.ran_as.account_name ?? 'Unknown account'}
                        <span className="text-muted-foreground">
                          {' '}({expandedRun.configuration.ran_as.email ?? `user ${expandedRun.configuration.ran_as.user_id}`})
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Not recorded</span>
                    )}
                  </div>

                  {typeof expandedRun.summary_stats?.error === 'string' && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      <span className="font-medium">Run failed:</span> {expandedRun.summary_stats.error}
                    </div>
                  )}

                  {expandedRun.results && expandedRun.results.length > 0 && (
                    <Table className="table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40%]">Case</TableHead>
                          <TableHead className="w-[10%]">Status</TableHead>
                          <TableHead className="w-[8%]">Score</TableHead>
                          <TableHead className="w-[10%]">Latency</TableHead>
                          <TableHead className="w-[32%]">Reasoning</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expandedRun.results.map((result) => (
                          <Fragment key={result.id}>
                            <TableRow
                              className={hasDetail(result.scoring_details) ? 'cursor-pointer' : ''}
                              onClick={() => {
                                if (hasDetail(result.scoring_details)) {
                                  setExpandedResultId(expandedResultId === result.id ? null : result.id);
                                }
                              }}
                            >
                              <TableCell className="text-sm">
                                <div className="line-clamp-2">
                                  {result.eval_case_name || result.eval_case_id.slice(0, 8) + '...'}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={STATUS_VARIANTS[result.status] || 'outline'}>
                                  {result.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm">
                                {result.score != null ? `${(result.score * 100).toFixed(0)}%` : '-'}
                              </TableCell>
                              <TableCell className="text-sm">
                                {result.latency_ms != null ? `${(result.latency_ms / 1000).toFixed(1)}s` : '-'}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                <div className="line-clamp-2">
                                  {isGuidedResult(result.scoring_details)
                                    ? (result.scoring_details.scope_key as string)
                                    : (result.scoring_details?.reasoning as string) || '-'}
                                </div>
                              </TableCell>
                            </TableRow>
                            {expandedResultId === result.id && isGuidedResult(result.scoring_details) && (
                              <TableRow>
                                <TableCell colSpan={5} className="p-0">
                                  <GuidedResultDetail details={result.scoring_details} />
                                </TableCell>
                              </TableRow>
                            )}
                            {expandedResultId === result.id && !!result.scoring_details?.reasoning && (
                              <TableRow>
                                <TableCell colSpan={5} className="p-0">
                                  <div className="bg-muted/50 rounded-md p-3 text-sm space-y-2 whitespace-normal break-words">
                                    <div>
                                      <span className="font-medium">Judge Reasoning: </span>
                                      {result.scoring_details.reasoning as string}
                                    </div>
                                    {(result.scoring_details.criteria_met as string[])?.length > 0 && (
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium">Criteria Met:</span>
                                        {(result.scoring_details.criteria_met as string[]).map((c) => (
                                          <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                                        ))}
                                      </div>
                                    )}
                                    {result.scoring_details.project_id != null && (
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium">Project fetched:</span>
                                        <Badge
                                          variant={result.scoring_details.tool_called ? 'default' : 'destructive'}
                                          className="text-xs"
                                        >
                                          {result.scoring_details.tool_called
                                            ? 'get_project_context called'
                                            : 'never called — answered without the project'}
                                        </Badge>
                                      </div>
                                    )}
                                    {(result.scoring_details.ground_truth as string[])?.length > 0 && (
                                      <div>
                                        <span className="font-medium">Ground truth read from the project: </span>
                                        <ul className="list-disc ml-6 mt-1">
                                          {(result.scoring_details.ground_truth as string[]).map((fact) => (
                                            <li key={fact} className="text-xs">{fact}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* New Run Dialog */}
      <Dialog open={showNewRun} onOpenChange={() => setShowNewRun(false)}>
        <DialogContent className="sm:!max-w-3xl">
          <DialogHeader>
            <DialogTitle>New Eval Run</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Run Name</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                placeholder="Weekly eval - Mar 4"
                value={runName}
                onChange={(e) => setRunName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Filter by Skill</label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                value={runSkillId}
                onChange={(e) => setRunSkillId(e.target.value)}
              >
                <option value="">All Skills (run all active test cases)</option>
                {skills.map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    {skill.display_title}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {runSkillId
                  ? 'Only test cases linked to this skill will be included.'
                  : 'All active test cases will be run through the Kaiya pipeline.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRun(false)}>Cancel</Button>
            <Button
              onClick={() => runName && createMutation.mutate({ name: runName, skill_template_id: runSkillId || undefined })}
              disabled={!runName || createMutation.isPending}
            >
              {createMutation.isPending ? 'Starting...' : 'Start Run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
